/**
 * Single source of truth for the agent-CRM navigation set.
 *
 * Extracted out of CrmSidebar.tsx (#1514, EPIC #1512) so the same grouped nav +
 * visibility predicate can be consumed by both the sidebar AND the universal
 * command/navigation registry (frontend/src/lib/commands/). Mirrors the
 * adminNav.ts pattern (#872).
 *
 * The rendered sidebar behaviour, visible groups, gating, accordion, icons,
 * labels and order are UNCHANGED — CrmSidebar.tsx now imports ``navGroups`` and
 * the shared ``isItemVisible`` predicate from here and renders exactly as before.
 */
import {
  Building2,
  ShieldCheck,
  Users,
  Map,
  Megaphone,
  MapPin,
  UserSearch,
  UserCheck,
  FileText,
  CalendarDays,
  CheckSquare,
  ClipboardList,
  MessageSquare,
  Archive,
  GraduationCap,
  LayoutDashboard,
  Scale,
  Handshake,
  FolderKanban,
  Receipt,
  ReceiptText,
  Hourglass,
  Wallet,
  AlarmClock,
  DollarSign,
  Target,
  Trophy,
  Plug,
  Contact,
  Home,
  Eye,
  BarChart3,
  Landmark,
  Shield,
  Inbox,
  Gavel,
  FileWarning,
  Settings,
  UserCog,
  ClipboardCheck,
  Lock,
  Link2,
  ListFilter,
  Upload,
  KeyRound,
} from "lucide-react";
import type { SidebarNavItem } from "./NavGroupSection";
import type { ModuleKey } from "../../contexts/modules";

export type PermFlag =
  | "capture_deal"
  | "view_commission"
  | "enter_own_commission"
  | "authorize_commission"
  | "process_payment"
  | "sign_off_deals"
  | "invest_funds"
  | "manage_users"
  | "view_management_dashboards"
  | "list_properties"
  | "manage_compliance"
  // Finance all-FICA visibility, area-assignment, maker-checker privileged
  // authorization requests, and canvasser add-only contact capability.
  | "view_all_fica"
  | "assign_areas"
  | "request_privileged_authorization"
  | "add_contact_numbers"
  // #2813: restricted HR/personnel document-template capability (per-user).
  | "manage_hr_documents";

export interface NavItem extends SidebarNavItem {
  /** When set, the item only renders if this module is enabled (D4-PKG-003). */
  module?: ModuleKey;
  /** When set, the item only renders if the user holds this flag (D4-AUTH-001). */
  flag?: PermFlag;
  /** When set, the item renders if the user holds ANY of these flags (D5-ASW-005). */
  anyFlag?: PermFlag[];
  /**
   * When set, the item is part of the agent's OWN sales book (contacts, farm,
   * listings, deals, own commission/targets...) and is therefore permanently
   * empty for a back-office persona whose data is not scoped to themselves.
   * It is hidden for the back-office presets (paralegal/finance/finance_accounts/
   * manager/accounts/office_admin) -- a standard field agent (preset "none") and
   * any CUSTOM preset keep it (#1178). Direct URLs remain server-protected; this
   * is nav-only. A back-office user who still works their own book
   * (``hasOwnBook``, D2-USR-010) keeps it too.
   */
  agentBookOnly?: boolean;
  /**
   * When set, the item is an AGENT-ACTIVITY surface (the agent's own stats
   * dashboard, viewings/appointments agenda) that the back-office presets do not
   * work -- hide it for them (#1267, the 11-June paralegal walkthrough: the
   * paralegal needs neither the agent Dashboard nor Appointments/viewings). A
   * plain field agent (preset "none") and any CUSTOM preset keep it. Distinct
   * from ``agentBookOnly`` (which is about own-record DATA being empty) -- these
   * surfaces are simply not part of a back-office role. Direct URLs remain
   * reachable; this is nav-only.
   */
  backOfficeHidden?: boolean;
  /**
   * D2-USR-010 (#4001): marks a ``backOfficeHidden`` item that is nonetheless
   * the user's OWN data (their viewings agenda, their own compliance
   * documents), so it stays visible for a back-office user who still works
   * their own book (``hasOwnBook``). Not set on "My Dashboard": for a manager
   * that page mixes the company-wide oversight boards, and the route sends a
   * back-office preset to its own landing regardless.
   */
  ownBookSurface?: boolean;
  /**
   * When set, the item ALSO shows for a holder of the ``list_properties`` flag
   * (D5-RBP-003, #1412) even if it would otherwise be hidden as ``agentBookOnly``.
   * Natalie's OFFICE_ADMIN (office/listing administrator) lists a property on
   * behalf of the agency, so it must see the Listings nav even though its data
   * is not self-scoped. The backend ``CanListProperties`` gate is the source of
   * truth; this only surfaces the entry point. Plain agents are unaffected (they
   * are not back-office, so they see the item via the normal ``agentBookOnly``
   * path regardless).
   */
  listingCapable?: boolean;
  /**
   * When set, the item stays visible for a holder of the ``manage_compliance``
   * flag (EPIC #2033 WP11, #2090) even when it would otherwise be hidden as
   * ``backOfficeHidden``. HR is a back-office preset (#2453) -- its own-book and
   * agent-activity surfaces are correctly bucketed away -- but HR still has its
   * OWN compliance documents (it owns the compliance module), so the self-
   * service "My Compliance" item must survive the back-office treatment. Mirrors
   * ``listingCapable`` (the office-admin Listings carve-out). The self-service
   * endpoint is server-scoped to ``request.user`` regardless; this only surfaces
   * the entry point. Non-HR back-office presets (no ``manage_compliance``) still
   * hide it.
   */
  complianceCapable?: boolean;
  /**
   * When set, the item stays visible for a holder of the ``add_contact_numbers``
   * flag (the CANVASSER preset, A5 #2287/#2467) even when it would otherwise be
   * hidden as ``agentBookOnly`` or ``backOfficeHidden``. A canvasser works the
   * farmbook + captures contacts + keeps a calendar/reminders, so Farm, Contacts
   * and Agenda survive the back-office treatment. The backend gates are the
   * source of truth; this only surfaces the entry points. Mirrors
   * ``listingCapable`` / ``complianceCapable`` (the office-admin Listings and HR
   * My-Compliance carve-outs). Non-canvasser back-office presets (no
   * ``add_contact_numbers``) still hide these items.
   */
  canvasserVisible?: boolean;
  /**
   * When set, the item is HIDDEN for a holder of the ``add_contact_numbers``
   * flag who is back-office -- i.e. the CANVASSER (#2738) -- even though a plain
   * agent, admins and every other preset keep it. This is the inverse of
   * ``canvasserVisible``: it removes a surface a canvasser must not reach (the
   * Document Vault) rather than retaining one. The backend gate is the source of
   * truth; this is nav-only (the matching route guard closes the direct-URL
   * hole). Mirrors the ``backOfficeHidden`` doc-comment style.
   */
  canvasserHidden?: boolean;
  /**
   * D2-FRM-018 (#3714): the item stays visible for an OFFICE CANVASSER
   * (``farm_wide_canvass``) even when it would otherwise be hidden as
   * ``agentBookOnly`` for a back-office preset -- the paralegal desk that
   * canvasses the whole farm book must reach Farm. Farm only: the flag grants
   * no listing, deal or paid-LOOM surface. The server scopes every farm call
   * through ``farm_area_ids_for_user``; this only surfaces the entry point.
   */
  officeCanvasserVisible?: boolean;
  /**
   * D2-LST-029 (#3722): the item stays visible for a back-office holder of THIS
   * flag even when it would otherwise be hidden as ``agentBookOnly``. It names
   * the exact capability the server uses to widen that surface's READ to the
   * company book, so the nav can never show a manager an empty page:
   *
   * * Listings -> ``sign_off_deals`` (``user_may_publish_listings`` /
   *   ``user_is_area_approver``: a manager reads the whole listing book and
   *   approves out-of-area listings);
   * * Deals -> ``view_management_dashboards``
   *   (``User.defaults_to_outstanding_deals_view``: a manager reads the company
   *   deal book, subject to the manager data floor).
   *
   * Only the MANAGER preset holds either flag among the back-office presets, so
   * finance / accounts / HR / paralegal / canvasser / office-admin keep the
   * own-book items hidden. Admins are not back-office and see them anyway.
   */
  oversightFlag?: PermFlag;
  /**
   * D2-LST-033 (#3749): the item stays visible for a back-office holder of THIS
   * flag even when it would otherwise be hidden as ``agentBookOnly`` -- the
   * READ-ONLY company-wide carve-out. Listings -> ``capture_deal``: the
   * paralegal / finance / accounts desks work every company deal file and must
   * be able to find its property, so the server reads them the whole listing
   * book on SAFE methods only (``PropertyViewSet._is_company_listing_reader``,
   * the ``has_company_case_reach`` rule). Every write stays owner-scoped.
   */
  companyReadFlag?: PermFlag;
  /**
   * #4157: the item is hidden for the paralegal desk (``paralegal`` and the
   * combined ``hr_paralegal`` seat -- ``worksParalegalDesk``) even though the
   * desk holds the item's ``flag``. Natalie's ruling: the conveyancer neither
   * adds partner firms nor sees who they are. Admins keep it. The backend gate
   * (``CanViewPartnerDirectory``) is the source of truth; ``PartnersRoute``
   * closes the direct URL.
   */
  paralegalDeskHidden?: boolean;
  /**
   * #4620: the item stays visible for the paralegal desk (``paralegal`` and the
   * combined ``hr_paralegal`` seat -- ``worksParalegalDesk``) even when it would
   * otherwise be hidden as ``agentBookOnly``. Deals: the server already reads
   * the desk the company's open deal book on that list
   * (``User.defaults_to_outstanding_deals_view``, B7 #2264), so the menu entry
   * is no longer an empty own-book page for them. The inverse of
   * ``paralegalDeskHidden``; nav-only -- no server read scope changes, and the
   * other back-office desks (finance, accounts, office-admin, HR) are
   * unaffected.
   */
  paralegalDeskVisible?: boolean;
  /**
   * D5-ASW-029: the item stays visible for the FINANCE desk (``finance`` and
   * the combined ``finance_accounts`` seat -- ``worksFinanceDesk``) even when
   * it would otherwise be hidden as ``agentBookOnly``. Deals: the server already
   * reads the desk the company's open deal book on that list
   * (``User.defaults_to_outstanding_deals_view`` via ``view_all_fica``, B7
   * #2264), and Finance opens any deal's splits, referral and documents from it
   * (D5-ASW-028) -- so the entry is never an empty own-book page for them.
   * Natalie's ruling (28 Oct 2026) restoring the item #4405 removed as a side
   * effect. Nav-only; accounts, office-admin, HR, canvasser and the paralegal
   * desk are unaffected.
   */
  financeDeskVisible?: boolean;
  /**
   * D4-FBK-4405 (#4405): the item is hidden for the FINANCE desk (``finance``
   * and the combined ``finance_accounts`` seat -- ``worksFinanceDesk``) even
   * though the desk holds the item's flag / company-read carve-out. Used on the
   * deal-desk work surfaces finance does not work (Listings, Templates,
   * Document Vault, Written Deals, Deals in Process). Admins keep them.
   * Nav-only: the server-side read scopes are unchanged; the matching route
   * guard (``FinanceDeskExcludedRoute``) closes the direct URL.
   */
  financeDeskHidden?: boolean;
}

export interface NavGroup {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  items: NavItem[];
}

// Grouped, scannable nav for non-technical agents.
export const navGroups: NavGroup[] = [
  {
    id: "workspace",
    label: "My Workspace",
    icon: Home,
    items: [
      // #1267: the agent Dashboard and the Agenda (viewings/valuations/meetings)
      // are agent-activity surfaces the back-office presets do not run -- hidden
      // for them (paralegal lands on /paralegal). Tasks stays: the paralegal
      // legitimately has a task list.
      {
        to: "/crm/dashboard",
        label: "My Dashboard",
        purpose: "Your own pipeline, targets and earnings",
        icon: LayoutDashboard,
        exact: true,
        backOfficeHidden: true,
      },
      { to: "/crm/tasks", label: "Tasks", purpose: "Your to-do list and reminders", icon: CheckSquare },
      {
        to: "/crm/appointments",
        label: "Agenda",
        purpose: "Viewings, valuations and meetings",
        icon: CalendarDays,
        backOfficeHidden: true,
        // #2467: the canvasser keeps a calendar/reminders for their canvassing
        // rounds, so the Agenda survives the back-office treatment.
        canvasserVisible: true,
        // D2-USR-010 (#4001): a back-office user who works their own book
        // books their own viewings.
        ownBookSurface: true,
      },
    ],
  },
  {
    id: "people",
    label: "Prospecting",
    icon: Contact,
    items: [
      // Own-book pages (#1178): scoped to the agent's own records, so they are
      // permanently empty for a back-office persona -- hidden for those presets.
      // #2467: Contacts + Farm stay visible for the CANVASSER (add_contact_numbers)
      // even though they are agentBookOnly -- a canvasser works the farmbook and
      // captures the people they meet.
      { to: "/crm/contacts", label: "Contacts", purpose: "Search, add and open your contacts", icon: Users, agentBookOnly: true, canvasserVisible: true },
      { to: "/crm/farm", label: "Farm", purpose: "Your farm area properties and owners", icon: Map, module: "farm", agentBookOnly: true, canvasserVisible: true, officeCanvasserVisible: true },
      // #3254: LOOM canvassing sits right after Farm -- same audience, same
      // area scope. canvasserVisible for the same reason Farm is: a canvasser
      // works allocated areas, and the server scopes every call identically.
      { to: "/crm/canvassing", label: "Canvassing", purpose: "Canvass your allocated areas", icon: Megaphone, module: "farm", agentBookOnly: true, canvasserVisible: true },
      // #3273: LOOM property lookup sits with Canvassing -- same LOOM surface,
      // same audience. It answers the pre-listing "who is the registered
      // owner?" question, which is a canvasser's job as much as an agent's.
      { to: "/crm/property-lookup", label: "Property lookup", purpose: "Find a property's registered owner", icon: MapPin, module: "farm", agentBookOnly: true, canvasserVisible: true },
      // Import farm book (#3442) — the OFFICE ADMIN (list_properties) and the
      // MANAGER (view_management_dashboards) load a farm book themselves. A
      // plain field agent and a canvasser hold NEITHER flag, so the item is
      // hidden for them (and CanImportFarmBook 403s the endpoint regardless).
      {
        to: "/crm/farm-book-import",
        label: "Import farm book",
        purpose: "Load a farm book spreadsheet",
        icon: Upload,
        module: "farm",
        anyFlag: ["list_properties", "view_management_dashboards"],
      },
      { to: "/crm/referrals", label: "Referrals", purpose: "Referrals you sent or received", icon: UserCheck, module: "farm", agentBookOnly: true },
      { to: "/crm/buyers", label: "Buyers", purpose: "Buyers and what they want", icon: UserSearch, agentBookOnly: true },
    ],
  },
  {
    id: "property-deals",
    label: "Property & Deals",
    icon: Building2,
    items: [
      // Own-book pages (#1178): hidden for the back-office presets.
      // D5-RBP-003 (#1412): listingCapable so the OFFICE_ADMIN (list_properties)
      // sees Listings to list a property, even though it is otherwise an
      // own-book (agentBookOnly) page.
      {
        to: "/crm/listings",
        label: "Listings",
        purpose: "Your listings and mandates",
        icon: Building2,
        agentBookOnly: true,
        listingCapable: true,
        // D2-LST-029 (#3722): managers approve out-of-area listings and publish
        // new ones from the Listings tabs -- the server reads them the whole
        // book on sign_off_deals.
        oversightFlag: "sign_off_deals",
        // D2-LST-033 (#3749): the back-office deal desks read (never edit)
        // the company listing book, so they can find a deal's property.
        companyReadFlag: "capture_deal",
        // D4-FBK-4405: not the finance desk (it reaches a deal's property
        // from the deal file, not the listing book).
        financeDeskHidden: true,
      },
      // #2877: propose-and-confirm farmbook erf-match review queue -- same
      // visibility as Listings itself (own-book + the office-admin listing
      // carve-out), since it is a listing-management tool.
      {
        to: "/crm/listings/farm-link-suggestions",
        label: "Farm Link Suggestions",
        purpose: "Confirm suggested farm matches",
        icon: Link2,
        agentBookOnly: true,
        listingCapable: true,
      },
      // D2-LST-029 (#3722): a manager reads the company deal book server-side
      // (view_management_dashboards) and signs deals off -- keep Deals for them.
      // #4620: the paralegal desk reads the company's open book on this list
      // too (B7 #2264), so it keeps Deals to find a deal by File No./address.
      {
        to: "/crm/deals",
        label: "Deals",
        purpose: "Your deals from offer to registration",
        icon: FileText,
        agentBookOnly: true,
        oversightFlag: "view_management_dashboards",
        paralegalDeskVisible: true,
        // D5-ASW-029: the finance desk works every deal's splits from here
        // (company-wide read, D5-ASW-028) -- Natalie, 28 Oct 2026.
        financeDeskVisible: true,
      },
      // #3720 (D2-OTP-026): Leases register -- every live lease + its renewal
      // lifecycle. Agents see their own leases, the back-office desks the whole
      // book (server-scoped), so it stays visible for both; hidden for a
      // canvasser (no deal surfaces).
      { to: "/crm/leases", label: "Leases", purpose: "Leases and when they are due for renewal", icon: KeyRound, canvasserHidden: true },
      { to: "/crm/fica-matrix", label: "FICA Matrix", purpose: "FICA status of your deal parties", icon: FileWarning, agentBookOnly: true },
      // Conveyancers directory is back-office only (#1025): field agents do
      // not need it. capture_deal is held by every back-office preset
      // (paralegal/accounts/finance/manager) and by admins (superset); a
      // plain agent has it False, so the item hides for them.
      { to: "/crm/conveyancers", label: "Conveyancers", purpose: "Conveyancing attorney directory", icon: Scale, flag: "capture_deal" },
      // D9-EXT-009: the partner directory (firms deals are shared with + their
      // partner links). Same back-office cohort; only with the client portal.
      {
        to: "/crm/partners",
        label: "Partners",
        purpose: "Partner firms deals are shared with",
        icon: Handshake,
        flag: "capture_deal",
        module: "client_portal",
        // #4157: not the conveyancing (paralegal) desk.
        paralegalDeskHidden: true,
      },
    ],
  },
  {
    id: "comms-docs",
    label: "Comms & Docs",
    icon: Inbox,
    items: [
      // Own-contacts messaging (#1178): empty for back-office -> hidden for those presets.
      { to: "/crm/messaging", label: "Messaging", purpose: "Send WhatsApp, SMS and e-mail", icon: MessageSquare, module: "messaging", agentBookOnly: true },
      // Letter/email templates (D2-DOC-052, #3968): a selling agent (preset
      // none/custom) uses them -- browse, preview, generate from their OWN
      // records -- so the item shows for every non-back-office user, and for a
      // back-office desk only when it holds capture_deal (paralegal / finance /
      // accounts / manager, unchanged). Office admin, HR and the canvasser stay
      // without it. Server-enforced (CanAccessTemplates + per-record ownership).
      // D4-FBK-4405: hidden for the finance desk (finance / finance_accounts).
      { to: "/crm/documents", label: "Templates", purpose: "Document templates to fill in", icon: FileText, module: "documents", agentBookOnly: true, companyReadFlag: "capture_deal", financeDeskHidden: true },
      // #2738: the shared company Document Vault is hidden for the CANVASSER
      // (add_contact_numbers, back-office) -- everyone else (plain agent /
      // office-admin / admin / other presets) keeps it. Route-guarded too.
      // D4-FBK-4405: hidden for the finance desk as well.
      { to: "/crm/document-vault", label: "Document Vault", purpose: "Stored documents, searchable", icon: Archive, module: "documents", canvasserHidden: true, financeDeskHidden: true },
      // #2813: restricted HR/personnel document templates -- a genuine
      // PER-USER grant (manage_hr_documents), never a preset. Hidden for
      // every user who lacks the flag (a plain agent, and every back-office
      // preset unless individually granted); admins hold it as a superset.
      { to: "/crm/hr-templates", label: "Personnel Templates", purpose: "Restricted HR document templates", icon: Lock, module: "documents", flag: "manage_hr_documents" },
      { to: "/crm/training", label: "Training", purpose: "Guided tours and training material", icon: GraduationCap, module: "training" },
    ],
  },
  {
    // Role landings (#452): only appear when the financial module is enabled
    // AND the user holds the matching flag. The group hides entirely otherwise.
    id: "legal-finance",
    label: "Legal & Finance",
    icon: Gavel,
    items: [
      {
        // D5-ASW-002: paralegal deals-to-process workspace (value-free).
        to: "/paralegal",
        label: "Written Deals",
        purpose: "Paralegal dashboard and every deal file, by stage",
        icon: FolderKanban,
        module: "financial",
        flag: "capture_deal",
        // D4-FBK-4405: the paralegal desk's workspace, not the finance desk's.
        financeDeskHidden: true,
      },
      {
        to: "/legal",
        label: "Deals in Process",
        purpose: "Open deal files ranked by deadline risk",
        icon: Scale,
        module: "financial",
        flag: "capture_deal",
        // D4-FBK-4405: the list page only -- a deal file (/legal/:id) stays
        // open to the finance desk.
        financeDeskHidden: true,
      },
      {
        // The combined Finance/Accounts workspace is the SINGLE money-queue nav
        // entry (one person at LWP does both), so there is deliberately no
        // separate "Finance Queue" / "Deposits to Invest" link -- a second link
        // would duplicate this surface and both would show active at once (the
        // /finance prefix is a substring of /finance-accounts).
        //
        // Visible to anyone holding EITHER capability (process_payment for the
        // commission-release half, invest_funds for the deposit-to-invest half),
        // so a finance-only or accounts-only preset still reaches it -- the page
        // itself renders only the section(s) the user's flags permit (each nested
        // queue keeps its own server-side gate). The underlying /finance and
        // /accounts routes still work directly; they simply have no separate nav.
        to: "/finance-accounts",
        label: "Finance/Accounts",
        purpose: "Commission release and deposit tasks",
        icon: Wallet,
        module: "financial",
        anyFlag: ["process_payment", "invest_funds"],
      },
      {
        // Payslips -- on-demand commission payslip generation (#984/#1014).
        // Finance (process_payment) runs the pay cycle, so it reaches the SAME
        // Payslips surface admins use, kept in the CRM shell (D5-RBP-003, #1413,
        // EPIC #1410). Same flag the backend (CanGeneratePayslips) enforces.
        to: "/crm/payslips",
        label: "Payslips",
        purpose: "Commission payslips",
        icon: Receipt,
        module: "financial",
        flag: "process_payment",
      },
      {
        // Commission Sheet (#2444): the monthly Finance/Super-User commission
        // workbook (#2201) -- one row per deal with splits + referral + company
        // net + received status + deductions. Mounted admin-shell at
        // /admin/commission-sheet; this Finance-reachable keep-in-shell mount
        // (/crm/commission-sheet) lets the FINANCE / FINANCE_ACCOUNTS preset
        // reach the SAME sheet. Gated on process_payment -- the SAME flag the
        // backend CanViewCommissionSheet (#2385) enforces; a commission-masked
        // preset is 403.
        to: "/crm/commission-sheet",
        label: "Commission Sheet",
        purpose: "Monthly commission workbook and export",
        icon: ReceiptText,
        module: "financial",
        flag: "process_payment",
      },
      {
        // Outstanding Deals register (#2463): the whole open book of
        // unregistered deals with commission-outstanding totals -- the in-app
        // replacement for the OUTSTANDING DEALS workbook. Mounted admin-shell
        // at /admin/outstanding-deals; this Finance-reachable keep-in-shell
        // mount (/crm/outstanding-deals, the #2444 pattern) lets FINANCE /
        // FINANCE_ACCOUNTS reach the SAME register. Gated on process_payment
        // -- the SAME flag the backend money gate (#2385) enforces.
        to: "/crm/outstanding-deals",
        label: "Outstanding Deals",
        purpose: "Unregistered deals and commission still due",
        icon: Hourglass,
        module: "financial",
        flag: "process_payment",
      },
      {
        // % of target -- Sales Targets (#2680). Finance asked to see "% of
        // target". The company sales-target roll-up (annual company target +
        // per-agent % achieved) lives at /crm/targets; the backend already
        // admits Finance (CanViewCommissionOrManagement) and the #2680
        // can_view_company_reporting scope now returns the COMPANY board to
        // Finance, but the nav entry lived only under My Stats (agentBookOnly,
        // hidden for the back-office Finance preset) and Oversight (management-
        // only). This Finance-reachable entry, gated on process_payment (the
        // same FINANCE / FINANCE_ACCOUNTS audience as the surfaces above), gives
        // Finance the path. A plain agent lacks process_payment, so it stays
        // under My Stats for them (no duplicate).
        to: "/crm/targets",
        label: "Sales Targets",
        purpose: "Sales targets and progress",
        icon: Target,
        module: "financial",
        flag: "process_payment",
      },
      {
        // % of target -- Company Leaderboard (#2680). The /crm/leaderboard board
        // shows every agent's % of target; the backend already admits Finance
        // (CanViewCommissionOrManagement) and renders the full ranked board. The
        // nav entry lived only under My Stats (agentBookOnly, hidden for Finance)
        // and Oversight (management-only), so this Finance-reachable entry gated
        // on process_payment surfaces it for FINANCE / FINANCE_ACCOUNTS.
        to: "/crm/leaderboard",
        label: "Leaderboard",
        purpose: "Agents ranked by sales",
        icon: Trophy,
        module: "financial",
        flag: "process_payment",
      },
      {
        // Registered & written sales -- Market Share (#2680). Finance asked for
        // "registered and written sales"; the Market Share board (per-area deal
        // counts + sale volumes, NO commission rand) is the in-app view. Mounted
        // admin-shell at /admin/market-share and management-shell at
        // /crm/oversight/market-share; this Finance-reachable keep-in-shell mount
        // (/crm/market-share, the #2444 pattern) lets FINANCE / FINANCE_ACCOUNTS
        // reach the SAME component. Gated on process_payment -- the SAME flag the
        // backend CanViewMarketShare (#2680) admits (alongside management).
        to: "/crm/market-share",
        label: "Market Share",
        purpose: "Our share of sales per area",
        icon: BarChart3,
        module: "financial",
        flag: "process_payment",
      },
      {
        // Bonds by Bank (#4323) -- bonds granted per bank + Ooba share (no
        // commission rand). The Finance-reachable mount of the board the
        // management shell has at /crm/oversight/bond-analytics; gated on
        // process_payment, the flag backend CanViewBondAnalytics admits.
        to: "/crm/bond-analytics",
        label: "Bonds by Bank",
        purpose: "Bond approvals by bank",
        icon: Landmark,
        module: "financial",
        flag: "process_payment",
      },
      {
        // The stage-1 "Finance approve" inbox (CommissionAuthorizationInbox).
        // The component offers ONLY the action the current user is entitled to
        // (finance-approve for process_payment; stage-2 authorize stays
        // admin/super-user). Gated on process_payment.
        to: "/finance/authorizations",
        label: "Commission Authorizations",
        purpose: "Approve commission for payment",
        icon: ShieldCheck,
        module: "financial",
        flag: "process_payment",
      },
      {
        // FICA Outstanding -- payment readiness. Finance needs sight of deals
        // whose required FICA is still outstanding -- no docs means the agent
        // cannot be paid -- so each is flagged "not ready for payment". Gated
        // on view_all_fica (FINANCE / FINANCE_ACCOUNTS; admins superset),
        // matching the backend CanViewAllFica gate.
        to: "/crm/finance/fica-outstanding",
        label: "FICA Outstanding",
        purpose: "Deals with FICA still outstanding",
        icon: FileWarning,
        module: "fica",
        flag: "view_all_fica",
      },
      {
        // Deals Lodged -- cash-flow feed. The company-wide, read-only sight of
        // deals lodged at the Deeds Office (on the runway to registration +
        // monies-received), gated on the same view_all_fica flag (FINANCE /
        // FINANCE_ACCOUNTS; admins superset) as FICA Outstanding.
        to: "/crm/finance/deals-lodged",
        label: "Deals Lodged",
        purpose: "Deals lodged at the Deeds Office",
        icon: Landmark,
        module: "financial",
        flag: "view_all_fica",
      },
      {
        // Deal Sign-off: the Manager preset's own landing page
        // (landingPathForUser -> /management). Same gating pattern as Deposits
        // to Invest -- visible to mgr (sign_off_deals) + admin only.
        to: "/management",
        label: "Deal Sign-off",
        purpose: "Sign off new deals",
        icon: Gavel,
        module: "financial",
        flag: "sign_off_deals",
      },
      {
        // D5-ASW-005: behind-schedule deals for the paralegal + weekly
        // management meeting (value-free). Reachable with capture_deal OR
        // sign_off_deals.
        to: "/behind-schedule",
        label: "Behind Schedule",
        purpose: "Deals running behind schedule",
        icon: AlarmClock,
        module: "financial",
        anyFlag: ["capture_deal", "sign_off_deals"],
      },
    ],
  },
  {
    // Management oversight (#1411, EPIC #1410): a MANAGER
    // (view_management_dashboards) oversees the business READ-ONLY -- the
    // operational dashboards (Monthly Summary, Deal / Attorney Pipeline, FICA,
    // KPI, Communications) + the company Leaderboard + Sales Targets, WITHOUT
    // the admin role. These point at keep-in-shell mounts of the SAME admin
    // dashboard components (#1153 / #1299 precedent). Every item gates on the
    // management flag, so the whole group is invisible to anyone else
    // (admins use the admin shell). Commission stays masked: the flag is NOT
    // view_commission and the backend keeps commission/payout endpoints on
    // their own gate, so the figures the manager sees here carry no rand.
    id: "oversight",
    label: "Oversight",
    icon: Eye,
    items: [
      {
        to: "/crm/oversight/monthly-deals",
        label: "Monthly Summary",
        purpose: "Read-only monthly deals summary",
        icon: CalendarDays,
        flag: "view_management_dashboards",
      },
      {
        to: "/crm/oversight/deal-pipeline",
        label: "Deal Pipeline",
        purpose: "Read-only deal pipeline",
        icon: BarChart3,
        flag: "view_management_dashboards",
      },
      {
        // #2823 (split from #2817): ONE master filterable view of the
        // outstanding deal book -- filter by category / agent / date range --
        // instead of several separately-labelled fixed buckets. Value-free.
        to: "/crm/oversight/deal-queue",
        label: "Deal Queue",
        purpose: "Deals waiting on the office",
        icon: ListFilter,
        flag: "view_management_dashboards",
      },
      {
        to: "/crm/oversight/pipeline-cashflow",
        label: "Pipeline / Cashflow",
        purpose: "Read-only expected commission cash",
        icon: Wallet,
        flag: "view_management_dashboards",
      },
      {
        to: "/crm/oversight/attorney-pipeline",
        label: "Attorney Pipeline",
        purpose: "Read-only deals by attorney",
        icon: Landmark,
        flag: "view_management_dashboards",
      },
      {
        to: "/crm/oversight/compliance",
        label: "FICA",
        purpose: "Read-only FICA overview",
        icon: Shield,
        module: "fica",
        flag: "view_management_dashboards",
      },
      {
        // FICA Outstanding payment-readiness (#2455): the manager oversees which
        // deals have outstanding required FICA (not-ready-for-payment) READ-ONLY,
        // as company oversight. Renders the same page as the finance
        // /crm/finance/fica-outstanding surface via a keep-in-shell mount; the
        // endpoint exposes FICA completeness counts only, never a commission rand.
        to: "/crm/oversight/fica-outstanding",
        label: "FICA Outstanding",
        purpose: "Deals with FICA still outstanding",
        icon: FileWarning,
        module: "fica",
        flag: "view_management_dashboards",
      },
      {
        to: "/crm/oversight/kpis",
        label: "KPI Reports",
        purpose: "Read-only KPI results",
        icon: Target,
        flag: "view_management_dashboards",
      },
      {
        // KPI Monthly Scorecard & outputs (WP5 #1788): the manager reads the
        // monthly scorecard / Involved-vs-Share / property detail / rolling
        // average read-only (RBAC inherited, never widened).
        to: "/crm/oversight/kpi-scorecard",
        label: "KPI Scorecard",
        purpose: "Read-only KPI scorecard",
        icon: BarChart3,
        flag: "view_management_dashboards",
      },
      {
        // KPI Exceptions / Data Issues (WP4 #1787): the manager reviews the
        // pre-lock data-issue report read-only (RBAC inherited, never widened).
        to: "/crm/oversight/kpi-exceptions",
        label: "KPI Exceptions",
        purpose: "KPI figures that need a check",
        icon: ClipboardList,
        flag: "view_management_dashboards",
      },
      {
        to: "/crm/oversight/communications",
        label: "Communications",
        purpose: "Read-only message delivery",
        icon: MessageSquare,
        module: "messaging",
        flag: "view_management_dashboards",
      },
      {
        // KPI Metrics catalogue (#2282): the manager VIEWS the metric catalogue
        // read-only; editing/publishing stays admin-only (server-enforced).
        to: "/crm/oversight/kpi-metrics",
        label: "KPI Metrics",
        purpose: "Read-only KPI list",
        icon: Target,
        flag: "view_management_dashboards",
      },
      {
        // KPI Tiers & Targets (#2282): read-only oversight of the per-tier
        // monthly targets + agent->tier assignment; edits stay admin-only.
        to: "/crm/oversight/kpi-tiers",
        label: "KPI Tiers",
        purpose: "Read-only KPI tiers",
        icon: Target,
        flag: "view_management_dashboards",
      },
      {
        // KPI Monthly Upload (#2282): the manager VIEWS the monthly source-file
        // ingestion pipeline read-only; upload/map/commit stay admin-only.
        to: "/crm/oversight/kpi-upload",
        label: "KPI Monthly Upload",
        purpose: "Upload monthly KPI figures",
        icon: FileText,
        flag: "view_management_dashboards",
      },
      {
        // Market Share (#2272): area count + sale-volume oversight (no commission
        // rand exposed), read-only.
        to: "/crm/oversight/market-share",
        label: "Market Share",
        purpose: "Read-only market share",
        icon: BarChart3,
        flag: "view_management_dashboards",
      },
      {
        // Activity Metrics (#2270): company-wide response-time / viewing /
        // valuation activity, read-only.
        to: "/crm/oversight/activity",
        label: "Activity Metrics",
        purpose: "Read-only agent activity",
        icon: ClipboardList,
        flag: "view_management_dashboards",
      },
      {
        // Agent Usage (#2270): per-agent login / records-added usage, read-only.
        to: "/crm/oversight/agent-usage",
        label: "Agent Usage",
        purpose: "Read-only CRM usage per agent",
        icon: Users,
        flag: "view_management_dashboards",
      },
      {
        // Bonds by Bank (#2270): bonds-by-granting-bank + originator share,
        // read-only.
        to: "/crm/oversight/bond-analytics",
        label: "Bonds by Bank",
        purpose: "Read-only bonds by bank",
        icon: Landmark,
        flag: "view_management_dashboards",
      },
      {
        // Areas (#2279): the area master list, read-only for the manager
        // (creating/editing/deleting areas stays admin-only, server-enforced).
        to: "/crm/oversight/areas",
        label: "Areas",
        purpose: "Read-only areas list",
        icon: Map,
        flag: "view_management_dashboards",
      },
      {
        // Mandate checklist (#2282): the mandate/FICA checklist catalogue, read-
        // only for the manager (editing stays admin-only, server-enforced).
        to: "/crm/oversight/checklist-templates",
        label: "Mandate Checklist",
        purpose: "Read-only mandate checklist",
        icon: ClipboardCheck,
        flag: "view_management_dashboards",
      },

      {
        // Agent Compliance (EPIC #2033 WP11 #2090): the manager OVERSEES the HR
        // compliance roster READ-ONLY -- everyone's status at a glance, the reason
        // code and how long outstanding, so they can hold HR accountable. The page
        // renders read-only for a manager (no upload/verify/reason-set); the
        // backend WRITE endpoints reject them regardless.
        to: "/crm/compliance/agents",
        label: "Agent Compliance",
        purpose: "Agent compliance documents and expiry",
        icon: ShieldCheck,
        flag: "view_management_dashboards",
      },
      {
        // Company Leaderboard -- the existing /crm/leaderboard route now
        // serves the manager the company-wide, rand-stripped board (#1411).
        to: "/crm/leaderboard",
        label: "Leaderboard",
        purpose: "Agents ranked by sales",
        icon: Trophy,
        module: "financial",
        flag: "view_management_dashboards",
      },
      {
        // Company Sales Targets -- turnover targets (not commission); the
        // existing /crm/targets route serves the company-wide view (#1411).
        to: "/crm/targets",
        label: "Sales Targets",
        purpose: "Sales targets and progress",
        icon: Target,
        flag: "view_management_dashboards",
      },
    ],
  },
  {
    // "My Commission" and "Leaderboard" belong to the financial module — when it
    // is disabled (D4-PKG-004) they disappear. They (and "My Targets" — sales
    // targets) are ALSO money/value surfaces, so they gate on the
    // ``view_commission`` flag (D5-RBP-001 / #1051): the value-free back-office
    // presets (paralegal/manager/accounts, view_commission=False) per Natalie's
    // "what paralegal does NOT require" list (8 June 2026) do not see commission,
    // the leaderboard, or sales targets. A standard field agent and fin_demo
    // (Finance) keep view_commission=True, so they are UNAFFECTED. "Monthly Activity" is
    // an activity barometer (days-on-market etc.), not a monetary figure, and
    // management explicitly want it, so it stays ungated.
    id: "my-stats",
    label: "My Stats",
    icon: Trophy,
    items: [
      {
        // Own-book money pages (#1178): for the back-office presets these are
        // always R0/empty (their commission powers live in the Finance Queue),
        // so they are also agentBookOnly -- this is what hides them from
        // fin_demo, whose view_commission=True would otherwise keep them shown.
        to: "/crm/commission",
        label: "My Commission",
        purpose: "Your commission earned and paid",
        icon: DollarSign,
        module: "financial",
        flag: "view_commission",
        agentBookOnly: true,
      },
      { to: "/crm/targets", label: "My Targets", purpose: "Sales targets and progress", icon: Target, flag: "view_commission", agentBookOnly: true },
      // Monthly Activity is own-book too (empty for back-office) -> hidden for those presets.
      { to: "/crm/kpis", label: "Monthly Activity", purpose: "Your monthly activity figures", icon: Target, agentBookOnly: true },
      // KPI agent confirmation loop (WP6, #1789): confirm your own figures.
      { to: "/crm/kpi-confirm", label: "Confirm KPIs", purpose: "Confirm your monthly KPI figures", icon: ClipboardCheck, agentBookOnly: true },
      {
        to: "/crm/leaderboard",
        label: "Leaderboard",
        purpose: "Agents ranked by sales",
        icon: Trophy,
        module: "financial",
        flag: "view_commission",
        agentBookOnly: true,
      },
    ],
  },
  {
    // Self-service settings (D2-USR-006). "Personal settings" lets the agent
    // edit their own profile + password; "Connect your AI" (the existing MCP
    // connect page) now lives here too.
    id: "settings",
    label: "Settings",
    icon: Settings,
    items: [
      // Scoped user management. Visible only to a manage_users holder — the
      // onboarding ranks Manager / Paralegal / HR (admins are a superset; the
      // Office Admin does not hold the flag). Routes to /crm/users (the
      // CRM-shell mount of the User & Team Management page); the backend
      // rank-hierarchy guard scopes which targets they may act on.
      {
        to: "/crm/users",
        label: "User Management",
        purpose: "Invite and manage users below you",
        icon: Users,
        flag: "manage_users",
      },
      // Agent compliance roster: HR (manage_compliance) OWNS the roster; the
      // manager reaches the same page READ-ONLY (it is also linked under
      // Oversight). The office-admin and paralegal do not see this entry --
      // the backend gates read vs write.
      {
        to: "/crm/compliance/agents",
        label: "Agent Compliance",
        purpose: "Agent compliance documents and expiry",
        icon: ShieldCheck,
        anyFlag: ["manage_compliance", "view_management_dashboards"],
      },
      // Agent self-service "My Documents" (EPIC #2033 WP3, #2036): the agent's
      // own compliance documents + status, the same record the office sees
      // (WP4). Self-scoped server-side. Hidden for the back-office presets
      // (paralegal/finance/manager/accounts/office_admin) -- compliance docs are
      // tracked against field agents; a plain agent and any CUSTOM preset keep
      // it. Direct URL stays reachable (server still scopes to request.user).
      {
        to: "/crm/my-documents",
        label: "My Compliance",
        purpose: "Upload your own compliance documents",
        icon: ShieldCheck,
        backOfficeHidden: true,
        // #2453: HR is a back-office preset but keeps its own compliance docs.
        complianceCapable: true,
        // D2-USR-010 (#4001): so does a back-office user who is still a
        // working agent (their compliance docs are tracked like any agent's).
        ownBookSurface: true,
      },
      { to: "/crm/settings", label: "Personal settings", purpose: "Your profile and password", icon: UserCog },
      { to: "/crm/connect", label: "Connect your AI", purpose: "Connect an AI assistant to the CRM", icon: Plug },
    ],
  },
];

/**
 * The back-office permission presets (#1178, + office_admin #1412, +
 * finance_accounts #1506). A back-office persona's data is NOT scoped to
 * themselves, so their own-book nav items (``agentBookOnly``) are permanently
 * empty -- hide them. The Listings item is the one exception for the
 * office-admin (``listingCapable`` + ``list_properties``); the manager keeps
 * Listings + Deals via ``oversightFlag`` (D2-LST-029). The standard field
 * agent (preset "none") and any CUSTOM preset (hybrids exist) keep the full nav.
 * HR (#2453, manage_compliance) is also back-office: its own-book / agent-activity
 * surfaces are hidden, but the self-service My Compliance item is retained via
 * ``complianceCapable`` since HR owns its own compliance documents.
 * CANVASSER (#2467, add_contact_numbers) is likewise back-office: its whole agent
 * sales-book / money / deal / management nav is hidden, but Farm, Contacts and
 * Agenda are retained via ``canvasserVisible`` since a canvasser works the
 * farmbook, captures contacts and keeps a calendar.
 */
export const BACK_OFFICE_PRESETS = [
  "paralegal",
  "finance",
  "finance_accounts",
  "manager",
  "accounts",
  "office_admin",
  "hr",
  // D5-RBP-005 (#4175): the combined HR/Paralegal desk.
  "hr_paralegal",
  "canvasser",
] as const;

/**
 * The presets that work the paralegal (conveyancing) desk -- mirrors the
 * backend's ``PARALEGAL_DESK_PRESETS``: the paralegal and the combined
 * HR/Paralegal seat. Never test ``preset === "paralegal"`` for desk membership.
 */
export const PARALEGAL_DESK_PRESETS = ["paralegal", "hr_paralegal"] as const;

/**
 * True for a non-admin who works the paralegal desk (#4157). An admin holds
 * every capability whatever preset string they carry, as the backend's
 * ``user_can_view_partner_directory`` does.
 */
export function worksParalegalDesk(
  role: string | null | undefined,
  preset: string | null | undefined,
): boolean {
  return role !== "admin" && (PARALEGAL_DESK_PRESETS as readonly string[]).includes(preset ?? "");
}

/**
 * The presets that work the FINANCE desk (#4394) -- mirrors the backend's
 * ``FINANCE_DESK_PRESETS``: Finance and the combined Finance/Accounts seat.
 * Never test ``preset === "finance"`` for desk membership.
 */
export const FINANCE_DESK_PRESETS = ["finance", "finance_accounts"] as const;

/**
 * True for a non-admin who works the finance desk (#4394). An admin is never
 * narrowed, whatever preset string they carry (as the backend's
 * ``desk_template_categories``).
 */
export function worksFinanceDesk(
  role: string | null | undefined,
  preset: string | null | undefined,
): boolean {
  return role !== "admin" && (FINANCE_DESK_PRESETS as readonly string[]).includes(preset ?? "");
}

/** True when the given permission preset string is a back-office persona. */
export function isBackOfficePreset(preset: string | null | undefined): boolean {
  return (BACK_OFFICE_PRESETS as readonly string[]).includes(preset ?? "");
}

/**
 * True when the given (preset, flag-check) pair belongs to the CANVASSER persona
 * (#2735/#2738). ``add_contact_numbers`` is an AGENT-BASELINE flag -- a PLAIN
 * agent also holds it -- so the flag ALONE does not identify a canvasser. Among
 * the back-office presets only the canvasser holds it (all others False), so the
 * robust discriminator is ``isBackOfficePreset(preset) && has(...)``: it
 * correctly EXCLUDES plain agents (not back-office), office-admin / other
 * back-office (flag False) and admins (not back-office). Non-nav callers (route
 * guards, FarmDetail) reuse this so the discriminator can never drift.
 */
export function isCanvasser(
  preset: string | null | undefined,
  has: (f: PermFlag) => boolean,
): boolean {
  return isBackOfficePreset(preset) && has("add_contact_numbers");
}

/**
 * True when the given (preset, flag-check) pair belongs to the OFFICE-ADMIN
 * persona (#2739/#2740/#2741). The office administrator is a listing PUBLISHER
 * / user-manager, NOT a selling agent: it holds ``list_properties`` but NOT
 * ``capture_deal`` (no own book of buyers/deals, no commission). Among the
 * back-office presets only the office-admin matches this signature --
 * paralegal / finance / accounts / manager all hold ``capture_deal``, and
 * hr / canvasser lack ``list_properties``. A PLAIN AGENT is NOT back-office, so
 * it keeps every agent surface regardless (crucially it must: a plain agent
 * also lacks ``capture_deal``, so the discriminator can NEVER key on the mere
 * ABSENCE of ``capture_deal``). The robust test is therefore
 * ``isBackOfficePreset(preset) && has("list_properties") && !has("capture_deal")``
 * -- a CAPABILITY test, never the persona string. Admins (not back-office) are
 * excluded too. Reused by the ListingDetail agent-only carve-outs (Matching
 * Buyers tab #2739, Schedule button #2740) and the /crm/kpis route guard
 * (#2741) so the discriminator can never drift.
 */
export function isOfficeAdmin(
  preset: string | null | undefined,
  has: (f: PermFlag) => boolean,
): boolean {
  return (
    isBackOfficePreset(preset) &&
    has("list_properties") &&
    !has("capture_deal")
  );
}

/**
 * D2-LST-033 (#3749): true for a caller who READS the company listing book but
 * may not change it -- a non-admin ``capture_deal`` holder (paralegal /
 * finance / accounts) who is neither a listing publisher (``list_properties``)
 * nor a manager (``sign_off_deals``). The Listings page hides its create /
 * bulk / mandate-fixing controls for them. The server is authoritative: the
 * company read is SAFE-methods only (``PropertyViewSet._is_company_listing_reader``)
 * and the listing detail carries its own ``viewer_read_only`` flag.
 */
export function isCompanyListingReader(
  role: string | null | undefined,
  has: (f: PermFlag) => boolean,
): boolean {
  return (
    role !== "admin" &&
    has("capture_deal") &&
    !has("list_properties") &&
    !has("sign_off_deals")
  );
}

/**
 * True when the nav-visibility ctx belongs to the CANVASSER persona
 * (``add_contact_numbers`` is an agent-baseline flag, so a canvasser is a
 * back-office preset that holds it). The nav ctx exposes ``isBackOffice``
 * directly (already resolved from the preset), so this mirrors ``isCanvasser``
 * for the ``isItemVisible`` predicate.
 */
export function isCanvasserContext(ctx: NavVisibilityContext): boolean {
  return ctx.isBackOffice && ctx.has("add_contact_numbers");
}

/**
 * The visibility context a consumer must supply. Carries everything the original
 * ``CrmSidebar.itemVisible`` closure read.
 */
export interface NavVisibilityContext {
  isEnabled: (module: ModuleKey) => boolean;
  has: (flag: PermFlag) => boolean;
  isBackOffice: boolean;
  /** D2-FRM-018: the user is an office canvasser (``farm_wide_canvass``). */
  farmWideCanvass?: boolean;
  /**
   * D2-USR-010 (#4001): the user still works their OWN book (server-computed
   * ``has_own_book`` on ``/auth/me``: at least one own farm area). A back-office
   * preset holder with an own book -- the field agent promoted to Manager for
   * View-as -- keeps the own-book items like a plain agent. Never true for a
   * canvasser.
   */
  hasOwnBook?: boolean;
  /** #4157: a non-admin on the paralegal desk (``worksParalegalDesk``). */
  paralegalDesk?: boolean;
  /**
   * D4-FBK-4405: a non-admin on the finance desk (``worksFinanceDesk``). The
   * finance desk never gets the own-book menu, and loses the
   * ``financeDeskHidden`` items.
   */
  financeDesk?: boolean;
}

/**
 * True when the ctx's user is a back-office preset holder who nonetheless works
 * their own book (D2-USR-010). The canvasser is excluded defensively (the server
 * never reports an own book for one -- their scope is borrowed from agents).
 * D4-FBK-4405 (#4405): the finance desk is excluded too -- a finance user with
 * farm areas on file still gets the finance menu, never the field-agent one
 * (the farm-area data itself is untouched).
 */
export function worksOwnBook(ctx: NavVisibilityContext): boolean {
  return Boolean(ctx.hasOwnBook) && !isCanvasserContext(ctx) && !ctx.financeDesk;
}

/**
 * The SHARED nav-item visibility predicate (#1514). This is the verbatim logic
 * the CrmSidebar used inline (formerly CrmSidebar.tsx lines ~456-463), lifted
 * here so the sidebar AND the command/navigation registry gate identically and
 * can never drift.
 *
 * D5-RBP-003 (#1412): a listing-capable item (Listings) stays visible for a
 * ``list_properties`` holder even when it would be hidden as own-book — the
 * office-admin lists on behalf of the agency.
 *
 * #2453: a compliance-capable item (My Compliance) stays visible for a
 * ``manage_compliance`` holder even when it would be hidden as
 * ``backOfficeHidden`` -- HR keeps its own compliance documents.
 *
 * #2467: a canvasser-visible item (Farm / Contacts / Agenda) stays visible for a
 * ``add_contact_numbers`` holder (the CANVASSER) even when it would be hidden as
 * ``agentBookOnly`` or ``backOfficeHidden`` -- a canvasser works the farmbook,
 * captures contacts and keeps a calendar.
 *
 * D2-FRM-018 (#3714): an ``officeCanvasserVisible`` item (Farm) stays visible
 * for an office canvasser (``farmWideCanvass``) even when it would be hidden as
 * ``agentBookOnly`` for their back-office preset.
 *
 * D2-LST-029 (#3722): an item with an ``oversightFlag`` (Listings, Deals)
 * stays visible for a back-office holder of that flag -- the manager -- even
 * when it would be hidden as ``agentBookOnly``.
 *
 * #2738: a ``canvasserHidden`` item (the Document Vault) is REMOVED for the
 * canvasser only -- the inverse carve-out -- while every non-canvasser context
 * keeps it (byte-for-byte unchanged).
 *
 * D2-USR-010 (#4001): a back-office user who still works their own book
 * (``hasOwnBook``) keeps every ``agentBookOnly`` item and the ``ownBookSurface``
 * agent-activity items (Agenda, My Compliance), as a plain agent does. The
 * ``flag`` / ``module`` gates still apply, so the value-free manager stays
 * without the commission/target money pages.
 *
 * #4157: a ``paralegalDeskHidden`` item (Partners) is removed for the paralegal
 * desk (``paralegalDesk``) only.
 *
 * #4620: a ``paralegalDeskVisible`` item (Deals) stays visible for the
 * paralegal desk even when it would be hidden as ``agentBookOnly``.
 *
 * D4-FBK-4405: a ``financeDeskHidden`` item is removed for the finance desk
 * (``financeDesk``) only.
 *
 * D5-ASW-029: a ``financeDeskVisible`` item (Deals) stays visible for the
 * finance desk even when it would be hidden as ``agentBookOnly``.
 */
export function isItemVisible(item: NavItem, ctx: NavVisibilityContext): boolean {
  return (
    (!item.module || ctx.isEnabled(item.module)) &&
    (!item.flag || ctx.has(item.flag)) &&
    (!item.anyFlag || item.anyFlag.some((f) => ctx.has(f))) &&
    (!item.agentBookOnly ||
      !ctx.isBackOffice ||
      worksOwnBook(ctx) ||
      (Boolean(item.listingCapable) && ctx.has("list_properties")) ||
      (Boolean(item.canvasserVisible) && ctx.has("add_contact_numbers")) ||
      (Boolean(item.officeCanvasserVisible) && Boolean(ctx.farmWideCanvass)) ||
      (item.oversightFlag !== undefined && ctx.has(item.oversightFlag)) ||
      (item.companyReadFlag !== undefined && ctx.has(item.companyReadFlag)) ||
      (Boolean(item.paralegalDeskVisible) && Boolean(ctx.paralegalDesk)) ||
      (Boolean(item.financeDeskVisible) && Boolean(ctx.financeDesk))) &&
    (!item.backOfficeHidden ||
      !ctx.isBackOffice ||
      (Boolean(item.ownBookSurface) && worksOwnBook(ctx)) ||
      (Boolean(item.complianceCapable) && ctx.has("manage_compliance")) ||
      (Boolean(item.canvasserVisible) && ctx.has("add_contact_numbers"))) &&
    (!item.canvasserHidden || !isCanvasserContext(ctx)) &&
    (!item.paralegalDeskHidden || !ctx.paralegalDesk) &&
    (!item.financeDeskHidden || !ctx.financeDesk)
  );
}
